import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DecisionEngineStatus from './DecisionEngineStatus'
import { executiveDecisionReasoningEngine } from '../../engines'
import { createCapitalAllocationProposal } from '../../../capital_allocation_proposal'
import {
    PROPOSAL_DRAFT_CREATED_MESSAGE,
    buildProposalHandoffPayload,
    getExistingProposalDraft,
    rememberProposalDraft
} from '../preparation/ProposalPreparation'
import { evaluateProposalPreparation } from '../../services/proposalPreparationReadiness'
import { EXECUTABLE_CANDIDATE_STATUS, synthesizeExecutableCandidate } from '../../services/synthesizeExecutableCandidate'
import {
    DELIVERY_CAPACITY_OPTIONS,
    INVESTMENT_HORIZON_OPTIONS,
    RISK_APPETITE_OPTIONS,
    createExecutiveConstraintsState,
    findConstraintLabel
} from '../../models/executiveConstraints'

const formatCurrency = (value, currency = 'EUR') =>
    Number.isFinite(Number(value))
        ? new Intl.NumberFormat('en', { style: 'currency', currency }).format(Number(value))
        : 'Not available from current evidence'

const readinessLabels = {
    READY_TO_FORMALIZE: ['Ready for Proposal', 'border-emerald-200 bg-emerald-50 text-emerald-800'],
    NEEDS_MANAGEMENT_INPUT: ['Needs Management Input', 'border-amber-200 bg-amber-50 text-amber-800'],
    BLOCKED: ['Not Ready', 'border-red-200 bg-red-50 text-red-800']
}

const MANAGEMENT_POSITIONS = Object.freeze({
    SUPPORT_CANDIDATE: 'SUPPORT_CANDIDATE',
    MODIFY_EXECUTIVE_CONSTRAINTS: 'MODIFY_EXECUTIVE_CONSTRAINTS',
    DEFER_DECISION: 'DEFER_DECISION',
    REJECT_CANDIDATE: 'REJECT_CANDIDATE'
})

const managementOptions = [
    {
        value: MANAGEMENT_POSITIONS.SUPPORT_CANDIDATE,
        label: 'Support Candidate',
        meaning:
            "Management agrees that the synthesized Executable Candidate should become the basis for a future Capital Allocation Proposal."
    },
    {
        value: MANAGEMENT_POSITIONS.MODIFY_EXECUTIVE_CONSTRAINTS,
        label: 'Modify Executive Constraints',
        meaning:
            'Management believes the current constraints do not reflect reality and wishes to revise them before evaluating the Candidate again.'
    },
    {
        value: MANAGEMENT_POSITIONS.DEFER_DECISION,
        label: 'Defer Decision',
        meaning:
            'Management concludes that no executive position should be adopted at this time while additional evidence, budget, or priorities are pending.'
    },
    {
        value: MANAGEMENT_POSITIONS.REJECT_CANDIDATE,
        label: 'Reject Candidate',
        meaning: 'Management concludes that the synthesized Candidate should not proceed. The Candidate remains available for review.'
    }
]

const candidateStatusLabels = {
    [EXECUTABLE_CANDIDATE_STATUS.NOT_AVAILABLE]: ['Not Available', 'border-red-200 bg-red-50 text-red-800'],
    [EXECUTABLE_CANDIDATE_STATUS.NEEDS_CONSTRAINTS]: ['Needs Constraints', 'border-amber-200 bg-amber-50 text-amber-800'],
    [EXECUTABLE_CANDIDATE_STATUS.CANDIDATE_AVAILABLE]: ['Candidate Available', 'border-emerald-200 bg-emerald-50 text-emerald-800'],
    [EXECUTABLE_CANDIDATE_STATUS.CONSTRAINT_CONFLICT]: ['Constraint Conflict', 'border-red-200 bg-red-50 text-red-800']
}
const hasText = (value) => typeof value === 'string' && value.trim()
const firstText = (...values) => values.find(hasText) || 'Not available from current evidence'

function MissingValue() {
    return <span className="text-sm font-medium text-amber-700">Not specified</span>
}

function ConstraintSelect({ label, value, options, onChange }) {
    const missing = !value
    return (
        <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</span>
            <select
                aria-label={label}
                className={`mt-1 block w-full rounded-lg border bg-white p-2 text-sm text-slate-900 ${
                    missing ? 'border-amber-300' : 'border-slate-300'
                }`}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            >
                <option value="">Not specified</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {missing && <span className="mt-1 block text-xs text-amber-700">Required value is missing.</span>}
        </label>
    )
}

function Field({ label, children }) {
    return (
        <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</dt>
            <dd className="mt-1 text-sm font-medium text-slate-900">{children}</dd>
        </div>
    )
}
function WorkspaceSection({ eyebrow, title, children, className = '' }) {
    return (
        <section className={`rounded-xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">{eyebrow}</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">{title}</h2>
            <div className="mt-5">{children}</div>
        </section>
    )
}

function EvidenceDetails({ recommendation, engine }) {
    const alternatives = recommendation?.evidence?.alternatives || recommendation?.alternatives || []
    return (
        <details className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <summary className="cursor-pointer text-sm font-semibold text-slate-800">Expand evidence lineage</summary>
            <dl className="mt-4 grid gap-4 sm:grid-cols-3">
                <Field label="Portfolio evidence">
                    {engine?.data?.id ||
                        engine?.execution?.candidateComparisonReference ||
                        'Available in Decision Center context'}
                </Field>
                <Field label="Candidate comparison">
                    {recommendation?.metadata?.supportingEvidenceReferences?.candidateComparisonReference ||
                        engine?.execution?.candidateComparisonReference ||
                        'Not available from current evidence'}
                </Field>
                <Field label="Alternatives evaluated">
                    {alternatives.length || 'Not available from current evidence'}
                </Field>
            </dl>
        </details>
    )
}

export default function ExecutiveDecisionWorkspace({
    executiveDecision,
    recommendation,
    engine,
    currency,
    loading = false,
    decisionCenterContext
}) {
    const navigate = useNavigate()
    const [management, setManagement] = useState({
        position: '',
        justification: '',
        strategicContextConfirmed: false,
        evidenceReviewed: false
    })
    const [creating, setCreating] = useState(false)
    const [creationError, setCreationError] = useState('')
    const [executiveConstraints, setExecutiveConstraints] = useState(createExecutiveConstraintsState)
    const executiveConstraintsRef = useRef(null)
    if (loading)
        return (
            <DecisionEngineStatus
                status="LOADING"
                diagnostics={{ message: 'Loading authoritative Capital Allocation recommendation…' }}
            />
        )
    if (!executiveDecision && engine?.diagnostics?.status && engine.diagnostics.status !== 'AVAILABLE')
        return <DecisionEngineStatus status={engine.diagnostics.status} diagnostics={engine.diagnostics} />
    const resolvedDecision =
        executiveDecision ||
        executiveDecisionReasoningEngine({
            portfolioEvidence: engine?.data,
            unconstrainedEconomicOptimization: engine?.data?.unconstrainedEconomicOptimization,
            constrainedEconomicOptimization: engine?.data?.constrainedEconomicOptimization,
            provenance: recommendation?.metadata
        })
    const authoritative = resolvedDecision?.recommendation
    const status = resolvedDecision?.status || 'UNAVAILABLE'
    if (status !== 'AVAILABLE' || !authoritative)
        return <DecisionEngineStatus status={status} diagnostics={{ diagnostics: resolvedDecision?.diagnostics }} />
    const context = decisionCenterContext || { executiveDecision: resolvedDecision, engines: { portfolio: engine } }
    const candidate = synthesizeExecutableCandidate({ decisionContext: context, executiveConstraints })
    const [candidateStatusLabel, candidateStatusClass] = candidateStatusLabels[candidate.status]
    const candidateAvailable = candidate.status === EXECUTABLE_CANDIDATE_STATUS.CANDIDATE_AVAILABLE
    const readinessManagement = {
        position:
            candidateAvailable && management.position === MANAGEMENT_POSITIONS.SUPPORT_CANDIDATE ? 'RECOMMENDATION' : '',
        strategicContextConfirmed: candidateAvailable && management.position === MANAGEMENT_POSITIONS.SUPPORT_CANDIDATE,
        evidenceReviewed: candidateAvailable && management.position === MANAGEMENT_POSITIONS.SUPPORT_CANDIDATE
    }
    const readiness = evaluateProposalPreparation(context, readinessManagement)
    const [readinessLabel, readinessClass] = readinessLabels[readiness.state]
    const capital = candidate.indicativeCapital
    const expectedImprovement = firstText(
        candidate.expectedResilienceImprovement,
        authoritative?.expectedOutcome,
        authoritative?.businessImpact,
        recommendation?.expectedImpact,
        recommendation?.rationale
    )
    const action = firstText(
        candidate.courseOfAction,
        authoritative?.headline,
        authoritative?.strategy?.title,
        recommendation?.title,
        'Executive capital allocation decision'
    )
    const blockers = readiness.blockers.length ? readiness.blockers : readiness.warnings
    const proposalActionAvailable =
        candidateAvailable && readiness.state === 'READY_TO_FORMALIZE' && management.position === MANAGEMENT_POSITIONS.SUPPORT_CANDIDATE
    const rationaleVisible = [
        MANAGEMENT_POSITIONS.REJECT_CANDIDATE,
        MANAGEMENT_POSITIONS.DEFER_DECISION
    ].includes(management.position)
    const update = (field, value) => setManagement((current) => ({ ...current, [field]: value }))
    const selectManagementPosition = (value) => {
        setManagement((current) => ({ ...current, position: value, justification: '' }))
        if (value === MANAGEMENT_POSITIONS.MODIFY_EXECUTIVE_CONSTRAINTS) {
            executiveConstraintsRef.current?.focus()
            executiveConstraintsRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
        }
    }
    const updateConstraint = (field, value) => setExecutiveConstraints((current) => ({ ...current, [field]: value }))
    const decisionCenterReference = firstText(context.reference, context.id, authoritative.id)
    const existingDraft = getExistingProposalDraft(decisionCenterReference)
    const openDraft = (draft = existingDraft) => {
        if (!draft) return
        navigate(`/capital-allocation-proposals/${draft.proposal_id}/versions/${draft.version}`, {
            replace: true,
            state: { message: PROPOSAL_DRAFT_CREATED_MESSAGE }
        })
    }
    const createProposal = async () => {
        if (existingDraft) {
            openDraft(existingDraft)
            return
        }
        setCreating(true)
        setCreationError('')
        try {
            const created = await createCapitalAllocationProposal(buildProposalHandoffPayload(context, management, candidate))
            rememberProposalDraft(decisionCenterReference, created)
            navigate(`/capital-allocation-proposals/${created.proposal_id}/versions/${created.version}`, {
                replace: true,
                state: { message: PROPOSAL_DRAFT_CREATED_MESSAGE }
            })
        } catch (error) {
            setCreationError(
                error?.response?.data?.detail ||
                    error?.message ||
                    'The Capital Allocation Proposal could not be created.'
            )
        } finally {
            setCreating(false)
        }
    }
    return (
        <div className="w-full space-y-6" data-testid="executive-decision-workspace">
            <WorkspaceSection eyebrow="Executive orientation" title="Executive Summary">
                <dl className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Decision under consideration">{action}</Field>
                    <Field label="Analytical evidence">
                        Portfolio optimization and supporting Decision Center evidence are available for Management
                        review.
                    </Field>
                    <Field label="Executable candidate">
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${candidateStatusClass}`}>
                            {candidateStatusLabel}
                        </span>
                    </Field>
                    <Field label="Proposal readiness">
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${readinessClass}`}>
                            {readinessLabel}
                        </span>
                    </Field>
                </dl>
            </WorkspaceSection>
            <WorkspaceSection
                eyebrow="Dominant decision object"
                title="Executable Candidate"
                className="border-cyan-300 shadow-md"
            >
                <dl className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                    <Field label="Synthesis status">
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${candidateStatusClass}`}>
                            {candidateStatusLabel}
                        </span>
                    </Field>
                    <Field label="Supported course of action">{candidateAvailable ? action : 'Not available from current evidence'}</Field>
                    <Field label="Source analytical position">{candidate.sourcePosition?.label || 'Not available from current evidence'}</Field>
                    <Field label="Indicative capital">{formatCurrency(capital, currency)}</Field>
                    <Field label="Expected resilience improvement">{expectedImprovement}</Field>
                    <Field label="Applied constraints">
                        {candidate.appliedConstraints?.filter(({ value }) => value !== null && value !== '').length || 0} of 4 captured locally
                    </Field>
                    <Field label="Remaining gaps">
                        {candidate.constraintGaps?.length ? candidate.constraintGaps.join(' ') : 'No unresolved constraint gaps.'}
                    </Field>
                    <Field label="Feasibility rationale">{candidate.feasibilityRationale}</Field>
                    <Field label="Evidence lineage">
                        {candidate.evidenceReferences?.map(({ label, reference }) => `${label}: ${reference}`).join('; ') ||
                            'Not available from current evidence'}
                    </Field>
                    <Field label="Proposal readiness">{readinessLabel}</Field>
                </dl>
            </WorkspaceSection>
            <WorkspaceSection eyebrow="Management judgement" title="Management Position">
                <p className="mb-3 text-sm leading-6 text-slate-600">
                    Capture Management's executive judgement regarding the current Executable Candidate. This local
                    Decision Center position creates no governance authority, proposal, approval, signature, audit trail,
                    workflow, or persisted record.
                </p>
                <p className="mb-4 rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-sm font-medium text-cyan-900">
                    Selecting &quot;Support Candidate&quot; does not create a Proposal. Proposal creation remains an explicit
                    Management action.
                </p>
                <fieldset className="grid gap-3 md:grid-cols-2">
                    <legend className="sr-only">Management Position</legend>
                    {managementOptions.map(({ value, label, meaning }) => (
                        <label key={value} className="rounded-lg border border-slate-200 p-4 text-sm text-slate-700">
                            <span className="flex gap-3 font-semibold text-slate-900">
                                <input
                                    type="radio"
                                    name="management-position"
                                    aria-label={label}
                                    checked={management.position === value}
                                    disabled={value === MANAGEMENT_POSITIONS.SUPPORT_CANDIDATE && !candidateAvailable}
                                    onChange={() => selectManagementPosition(value)}
                                />
                                {label}
                            </span>
                            <span className="mt-2 block leading-6">{meaning}</span>
                        </label>
                    ))}
                </fieldset>
                {rationaleVisible && (
                    <label className="mt-4 block text-sm font-medium text-slate-700">
                        Executive Rationale
                        <textarea
                            aria-label="Executive Rationale"
                            className="mt-1 block min-h-20 w-full rounded-lg border border-slate-300 p-2"
                            value={management.justification}
                            onChange={(event) => update('justification', event.target.value)}
                        />
                    </label>
                )}
            </WorkspaceSection>
            <WorkspaceSection eyebrow="Proposal gate" title="Proposal Readiness">
                <span className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${readinessClass}`}>
                    {readinessLabel}
                </span>
                {blockers.length > 0 && (
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        {blockers.map(({ category, message }) => (
                            <li key={`${category}-${message}`}>
                                <strong>{category}:</strong> {message}
                            </li>
                        ))}
                    </ul>
                )}
                {proposalActionAvailable && (
                    <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-sm text-emerald-800">
                            Readiness allows creation of the formal governance artifact.
                        </p>
                        <p className="mt-2 text-sm font-medium text-emerald-900">
                            Decision Center only prepares the initial Draft. Proposal owns editing, completion, submission,
                            and versioning after creation.
                        </p>
                        {creationError && (
                            <p role="alert" className="mt-3 text-sm font-medium text-red-700">
                                {creationError}
                            </p>
                        )}
                        {existingDraft ? (
                            <>
                                <p className="mt-3 text-sm text-emerald-800">
                                    A Draft created during this browser session is available for this Decision Center
                                    reference.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => openDraft()}
                                    className="mt-4 rounded-lg bg-emerald-800 px-5 py-2.5 text-sm font-semibold text-white"
                                >
                                    Open Existing Draft
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                disabled={creating}
                                onClick={createProposal}
                                className="mt-4 rounded-lg bg-emerald-800 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                            >
                                {creating ? 'Creating proposal…' : 'Create Capital Allocation Proposal'}
                            </button>
                        )}
                    </div>
                )}
            </WorkspaceSection>
            <WorkspaceSection eyebrow="Executive input" title="Executive Constraints">
                <div ref={executiveConstraintsRef} tabIndex={-1} className="sr-only">
                    Executive Constraints focus target
                </div>
                <p className="mb-4 text-sm leading-6 text-slate-600">
                    Constraint inputs are local to this Decision Center session, are not saved, and are not connected to
                    synthesis. Entering values does not create a Management recommendation.
                </p>
                <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <label className="block">
                        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Available Capital
                        </span>
                        <input
                            aria-label="Available Capital"
                            type="number"
                            min="0"
                            step="1"
                            inputMode="decimal"
                            className={`mt-1 block w-full rounded-lg border p-2 text-sm text-slate-900 ${
                                executiveConstraints.availableCapital === '' ? 'border-amber-300' : 'border-slate-300'
                            }`}
                            value={executiveConstraints.availableCapital}
                            onChange={(event) => updateConstraint('availableCapital', event.target.value)}
                            placeholder="Not specified"
                        />
                        {executiveConstraints.availableCapital === '' ? <MissingValue /> : null}
                    </label>
                    <ConstraintSelect
                        label="Investment Horizon"
                        value={executiveConstraints.investmentHorizon}
                        options={INVESTMENT_HORIZON_OPTIONS}
                        onChange={(value) => updateConstraint('investmentHorizon', value)}
                    />
                    <ConstraintSelect
                        label="Delivery Capacity"
                        value={executiveConstraints.deliveryCapacity}
                        options={DELIVERY_CAPACITY_OPTIONS}
                        onChange={(value) => updateConstraint('deliveryCapacity', value)}
                    />
                    <ConstraintSelect
                        label="Risk Appetite"
                        value={executiveConstraints.riskAppetite}
                        options={RISK_APPETITE_OPTIONS}
                        onChange={(value) => updateConstraint('riskAppetite', value)}
                    />
                </div>
                <dl className="mt-4 grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Available Capital">
                        {executiveConstraints.availableCapital === ''
                            ? 'Not specified'
                            : formatCurrency(executiveConstraints.availableCapital, currency)}
                    </Field>
                    <Field label="Investment Horizon">
                        {findConstraintLabel(INVESTMENT_HORIZON_OPTIONS, executiveConstraints.investmentHorizon) ||
                            'Not specified'}
                    </Field>
                    <Field label="Delivery Capacity">
                        {findConstraintLabel(DELIVERY_CAPACITY_OPTIONS, executiveConstraints.deliveryCapacity) ||
                            'Not specified'}
                    </Field>
                    <Field label="Risk Appetite">
                        {findConstraintLabel(RISK_APPETITE_OPTIONS, executiveConstraints.riskAppetite) ||
                            'Not specified'}
                    </Field>
                </dl>
            </WorkspaceSection>
            <WorkspaceSection eyebrow="Supporting evidence" title="Analytical Evidence Summary">
                <p className="text-sm leading-6 text-slate-600">
                    Analytical evidence is read-only support for the executive decision workspace and no longer
                    dominates the page.
                </p>
                <div className="mt-4">
                    <EvidenceDetails recommendation={recommendation} engine={engine} />
                </div>
            </WorkspaceSection>
        </div>
    )
}
